// SummaryStrip — four-tile header above the deliverables list:
//   Overdue / Due this week / Failed acceptance / % on time.
// All counts go through `deriveDeliverableStatus` so the tile counts,
// the filter chip badges, and the contract-overview Overdue card cannot
// drift.
//
// Counting rules:
//   Overdue tile                = derived `overdue` ONLY (true overdue,
//                                 supplier chase queue). Uses a custom
//                                 red-gradient card mirroring the
//                                 milestone "Slipped" tile so the
//                                 active-failure signal carries weight.
//   Due this week tile          = forward-looking, status `not-started`
//                                 only.
//   Failed acceptance tile      = sum of failedAcceptanceCount across
//                                 countable deliverables. Subtitle
//                                 splits into deliverable-count and the
//                                 worst (max) attempt-count so the
//                                 contract-manager can triage the
//                                 cohort that's bounced acceptance
//                                 most often. Click toggles the
//                                 has-failed-acceptance summary filter.
//   % on time tile              = numerator: submissions on or before
//                                 due; denominator: items where we have
//                                 a signal (submitted OR past due).
//                                 Tone gates on the percentage.

import MetricCard from "./MetricCard";
import DeliverableSlipTag from "./DeliverableSlipTag";
import type { SummaryFilterKey } from "./applySummaryFilter";
import { deriveDeliverableStatus } from "./deriveDeliverableStatus";
import type { Deliverable } from "../../../../types";

export interface SummaryStripProps {
  deliverables: Deliverable[];
  today?: Date;
  /**
   * When supplied, each tile becomes a filter-toggle button. The
   * callback receives the card's filter key, or `null` when clicking
   * the currently-active tile (toggle-off). Without this prop the
   * tiles stay non-interactive (the legacy read-only mode).
   */
  onFilterChange?: (filter: SummaryFilterKey | null) => void;
  /** Which tile is currently active — drives the ring + aria-pressed. */
  activeFilter?: SummaryFilterKey | null;
}

const DAY_MS = 86_400_000;

function startOfUTCDay(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function isoToUTCDay(iso: string): number | null {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function dayName(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-AU", {
    weekday: "long",
    timeZone: "UTC",
  });
}

function isCountable(d: Deliverable): boolean {
  // Recurring parents are schedule templates, not actionable items —
  // their occurrences are what the user actions, so the parent record
  // never counts toward live tiles regardless of its own dueDate/status.
  if (d.archivedAt) return false;
  if (d.recurrenceRule) return false;
  return true;
}

function onTimeTone(
  percentage: number,
  denominator: number,
): "success" | "warning" | "danger" | "neutral" {
  if (denominator === 0) return "neutral";
  if (percentage >= 90) return "success";
  if (percentage >= 70) return "warning";
  return "danger";
}

export default function SummaryStrip({
  deliverables,
  today = new Date(),
  onFilterChange,
  activeFilter,
}: SummaryStripProps) {
  if (deliverables.length === 0) return null;

  const handleToggle = onFilterChange
    ? (key: SummaryFilterKey) =>
        onFilterChange(activeFilter === key ? null : key)
    : undefined;

  const todayMs = startOfUTCDay(today);
  const sevenDaysMs = todayMs + 7 * DAY_MS;

  // Overdue tile: derived `overdue` ONLY — supplier chase queue.
  // `late` items are customer review (delayed delivery) and surface
  // through the status / Late filter chips.
  const overdueItems = deliverables.filter((d) => {
    if (!isCountable(d)) return false;
    const derived = deriveDeliverableStatus(
      d.status,
      d.dueDate,
      today,
      d.submittedAt,
    );
    return derived === "overdue";
  });
  const overdueCount = overdueItems.length;
  // Worst (max) days overdue for the headline slip pill.
  const worstOverdueDays = overdueItems.reduce((max, d) => {
    const due = isoToUTCDay(d.dueDate as string);
    if (due === null) return max;
    const days = Math.round((todayMs - due) / DAY_MS);
    return days > max ? days : max;
  }, 0);

  // Due this week: not-started items whose dueDate sits in [today, today+7d].
  const dueThisWeekItems = deliverables
    .filter((d) => {
      if (!isCountable(d)) return false;
      if (d.status !== "not-started" || !d.dueDate) return false;
      const due = isoToUTCDay(d.dueDate);
      return due !== null && due >= todayMs && due <= sevenDaysMs;
    })
    .sort((a, b) => {
      if (a.dueDate! < b.dueDate!) return -1;
      if (a.dueDate! > b.dueDate!) return 1;
      return a.id < b.id ? -1 : 1;
    });
  const dueThisWeekCount = dueThisWeekItems.length;
  const earliest = dueThisWeekItems[0];

  // Failed acceptance: total bounce count across countable
  // deliverables. Subtitle splits into the count of distinct
  // deliverables that have bounced (deliverableCount) and the worst
  // (max) attempts seen on any one deliverable so the contract
  // manager can spot a single deliverable in serial trouble vs
  // many one-off slips.
  let failedTotal = 0;
  let failedDeliverableCount = 0;
  let worstFailedAttempts = 0;
  for (const d of deliverables) {
    if (!isCountable(d)) continue;
    const n = d.failedAcceptanceCount ?? 0;
    if (n <= 0) continue;
    failedTotal += n;
    failedDeliverableCount += 1;
    if (n > worstFailedAttempts) worstFailedAttempts = n;
  }

  // % on time:
  //   numerator   = items submitted on or before dueDate
  //   denominator = items where we have a signal (submitted at all OR
  //                 already past due)
  // Items with no dueDate are excluded — we have no on-time question
  // to ask. Items with a dueDate in the future and no submission are
  // excluded too — they're still in-flight.
  let onTimeNumerator = 0;
  let onTimeDenominator = 0;
  const todayIso = new Date(todayMs).toISOString().slice(0, 10);
  for (const d of deliverables) {
    if (!isCountable(d)) continue;
    if (!d.dueDate) continue;
    const submitted = d.submittedAt ? d.submittedAt.slice(0, 10) : null;
    const isPastDue = d.dueDate < todayIso;
    if (!submitted && !isPastDue) continue;
    onTimeDenominator += 1;
    if (submitted && submitted <= d.dueDate) onTimeNumerator += 1;
  }
  const onTimePercent =
    onTimeDenominator === 0
      ? 0
      : Math.round((onTimeNumerator / onTimeDenominator) * 100);

  return (
    <div
      data-testid="deliverable-summary-strip"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
    >
      {/* Overdue — promoted card with red border + gradient + worst-slip
       *  headline when count > 0. When count = 0 the tile falls back to
       *  the neutral slate palette used by the other inactive tiles so the
       *  baseline doesn't carry an active-failure signal. */}
      <button
        type="button"
        data-testid="metric-card-overdue"
        aria-pressed={activeFilter === "overdue"}
        onClick={
          handleToggle ? () => handleToggle("overdue") : undefined
        }
        disabled={!handleToggle}
        className={`text-left rounded-md border p-4 transition-shadow focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 ${
          overdueCount > 0
            ? "border-red-200 bg-linear-to-b from-red-500/5 to-transparent dark:border-red-900/60"
            : "border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800/60"
        } ${
          handleToggle ? "hover:shadow-sm cursor-pointer" : "cursor-default"
        } ${
          activeFilter === "overdue"
            ? "ring-2 ring-brand-400"
            : ""
        }`}
      >
        <div className="text-caption font-medium text-slate-500 dark:text-slate-400">
          Overdue
        </div>
        <div
          data-testid="metric-card-overdue-value"
          className={`mt-1 text-h2 font-semibold ${
            overdueCount > 0
              ? "text-red-600 dark:text-red-400"
              : "text-slate-700 dark:text-slate-200"
          }`}
        >
          {overdueCount}
        </div>
        {overdueCount > 0 && worstOverdueDays > 0 && (
          <div className="mt-2">
            <DeliverableSlipTag
              variant="late"
              days={worstOverdueDays}
              headline
            />
            <span className="ml-2 text-caption text-slate-500 dark:text-slate-400">
              worst overdue
            </span>
          </div>
        )}
      </button>

      <MetricCard
        testId="metric-card-due-this-week"
        label="Due this week"
        value={dueThisWeekCount}
        valueTone={dueThisWeekCount > 0 ? "warning" : "neutral"}
        subtitle={
          earliest
            ? `next: ${earliest.name} · ${dayName(earliest.dueDate as string)}`
            : undefined
        }
        onClick={
          handleToggle ? () => handleToggle("due-this-week") : undefined
        }
        isActive={activeFilter === "due-this-week"}
      />

      <MetricCard
        testId="metric-card-has-failed-acceptance"
        label="Failed acceptance"
        value={failedTotal}
        // Danger when any deliverable has bounced — failed acceptance
        // is the clearest leading indicator that a deliverable is in
        // trouble. Neutral when zero so the baseline doesn't carry an
        // active-failure signal.
        valueTone={failedTotal > 0 ? "danger" : "neutral"}
        subtitle={
          failedTotal > 0
            ? `across ${failedDeliverableCount} deliverable${failedDeliverableCount === 1 ? "" : "s"} · worst ${worstFailedAttempts} failure${worstFailedAttempts === 1 ? "" : "s"}`
            : "no failed acceptance recorded"
        }
        onClick={
          handleToggle
            ? () => handleToggle("has-failed-acceptance")
            : undefined
        }
        isActive={activeFilter === "has-failed-acceptance"}
      />

      <MetricCard
        testId="metric-card-on-time"
        label="% on time"
        value={onTimeDenominator === 0 ? "—" : `${onTimePercent}%`}
        valueTone={onTimeTone(onTimePercent, onTimeDenominator)}
        subtitle={
          onTimeDenominator === 0
            ? "no submissions or past-due items yet"
            : `${onTimeNumerator} of ${onTimeDenominator} submitted on or before due date`
        }
      />
    </div>
  );
}
